//libs
import React, { Dispatch, memo, SetStateAction, useState } from 'react';

//helpers
import vibrate from 'shared/helpers/dom/vibrate';

//components
import DeskTopSizeSelector from 'shared/layouts/Wishlist/components/SizeSelector/Desktop';
import Notify from 'shared/components/Notify/Mobile';
import AddToBag from 'shared/layouts/DetailsPage/components/ProductInfo/components/AddToBag';
import SizeSelectorMobile from 'shared/components/SizeSelector';

//hooks
import { useWishlistContext } from 'shared/layouts/Wishlist/API/context';

//analytics
import {
  sizeSelected as sizeSelectedAnalytics,
  trackNotifyMeClicked,
} from 'shared/layouts/Wishlist/analytics';
import {
  InverseLoader,
  MoveToBagButton,
} from 'shared/layouts/Wishlist/components/SizeSelector/styles';
import { getDeviceType } from 'shared/components/ResponsiveLoader/helpers';
import { RENDER_MODE } from 'shared/constants';
import { useDispatch, useSelector } from 'react-redux';
import { setCartNudge } from 'shared/store/app/actions';
import { getCartNudgeCount } from 'shared/store/app/selectors';

interface SizeSelectorProps {
  onHide: VoidFunction;
  product: Listing.IProduct | undefined;
  selectedSize: ProductDetail.ISizeOption | undefined;
  setSelectedSize: Dispatch<SetStateAction<ProductDetail.ISizeOption | undefined>>;
}

const TEXT = {
  MOVE_TO_BAG: 'Move To Bag',
  ADD_TO_BAG: 'Add To Bag',
};

const SizeSelector = ({ onHide, product, selectedSize, setSelectedSize }: SizeSelectorProps) => {
  const isMobile = getDeviceType() === RENDER_MODE.MOBILE;

  const [shakeSizeSelector, setShakeSizeSelector] = useState(false);

  const { removeProductFromWishlist } = useWishlistContext();
  const dispatch = useDispatch();
  const cartNudgeCount = useSelector(getCartNudgeCount);

  const selectSizeSelectorOverlay = () => {
    setShakeSizeSelector(false);
    setTimeout(() => {
      vibrate(500);
      setShakeSizeSelector(true);
    }, 0);
  };
  const onAddToCartAction = selectedSize
    ? () => {
        removeProductFromWishlist(product?.productId).then(() => {
          dispatch(setCartNudge(cartNudgeCount + 1));
          onHide();
        });
      }
    : selectSizeSelectorOverlay;

  const sizeSelectHandler = (size: ProductDetail.ISizeOption | undefined) => {
    if (!selectedSize) {
      sizeSelectedAnalytics();
    }
    setSelectedSize(size);
  };

  const handleOnNotified = () => {
    trackNotifyMeClicked({
      productId: product?.productId,
      price: product?.mrp,
      discountedPrice: product?.discountedPrice,
      discount: product?.discount,
      type: product?.type,
      categoryLevels: product?.primary_categories_json_all,
      isProductOffersAvailable: 'NA',
      variantType: selectedSize?.sizeName,
      variantProductID: selectedSize?.id,
      productData: selectedSize,
    });
  };

  return isMobile ? (
    <SizeSelectorMobile
      isAddedToCart={false}
      productID={product?.productId}
      onHide={onHide}
      onSizeSelect={sizeSelectHandler}
      hideBodyMeasurements={false}
      isOutOfStock={product?.isOutOfStock}
      onAddToCartClick={() => removeProductFromWishlist(product?.productId)}
      isWishList
      selectedSize={selectedSize}
      onNotified={handleOnNotified}
    />
  ) : (
    <DeskTopSizeSelector
      setSelectedSize={setSelectedSize}
      selectedSize={selectedSize}
      shake={shakeSizeSelector}
      onHide={onHide}
      product={product}
    >
      {selectedSize?.isOutOfStock || product?.isOutOfStock ? (
        <Notify
          showInlineform
          showHelperText={false}
          className={'desktop-notify'}
          productId={product?.productId}
          onNotified={handleOnNotified}
        />
      ) : selectedSize ? (
        <AddToBag
          hideToast
          disabledOverride
          data-test="add-to-bag"
          isAddedToCart={false}
          onClick={onAddToCartAction}
          selectedSize={selectedSize}
          hideShowSizeSelector={onHide}
          render={({ addToCartForUser, inProgress }) => (
            <MoveToBagButton size="large" onClick={addToCartForUser} shape="default" kind="primary">
              {inProgress ? <InverseLoader /> : TEXT.MOVE_TO_BAG}
            </MoveToBagButton>
          )}
        />
      ) : (
        <MoveToBagButton kind="secondary" size="medium" shape="default" onClick={onAddToCartAction}>
          {TEXT.MOVE_TO_BAG}
        </MoveToBagButton>
      )}
    </DeskTopSizeSelector>
  );
};

export default memo(SizeSelector);
